/**
 * @fileoverview Toast celebratorio cuando se desbloquea una feature
 * @module components/features/UnlockToast
 */

import { FeatureName } from '../../types/features';
import { getFeatureInfo, getFeatureRoute } from '../../utils/featureInfo';

/**
 * Muestra un toast celebratorio cuando se desbloquea una feature automáticamente
 * Nota: Este método debe ser llamado desde un componente que tenga acceso a useToast
 */
export function showUnlockToast(featureName: FeatureName): void {
  const feature = getFeatureInfo(featureName);
  const route = getFeatureRoute(featureName);
  
  // Crear elemento personalizado para el toast
  const toastContent = document.createElement('div');
  toastContent.className = 'flex items-start gap-4 p-2';
  
  toastContent.innerHTML = `
    <div class="text-4xl">${feature.icon}</div>
    <div class="flex-1">
      <p class="font-bold text-base mb-1">🎉 ¡Nueva herramienta desbloqueada!</p>
      <p class="font-semibold text-brand-600 dark:text-brand-400 mb-2">
        ${feature.icon} ${feature.title}
      </p>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
        ${feature.shortDescription}
      </p>
      ${route ? `
        <a 
          href="${route}"
          class="inline-block px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium mr-2"
        >
          ✨ Ver Ahora
        </a>
      ` : ''}
    </div>
  `;
  
  // Usar el sistema de toast nativo de la app
  // Esta función será mejorada cuando se integre en componentes con useToast
  console.log(`🎉 Feature desbloqueada: ${feature.title}`);
  
  // Mostrar notificación del navegador si está disponible
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('🎉 Nueva herramienta desbloqueada', {
      body: `${feature.icon} ${feature.title}\n${feature.shortDescription}`,
      icon: '/logo.png',
      tag: `feature-unlock-${featureName}`
    });
  }
}

/**
 * Componente toast para preview/testing
 */
export const UnlockToastPreview: React.FC<{ featureName: FeatureName }> = ({ featureName }) => {
  return (
    <button
      onClick={() => showUnlockToast(featureName)}
      className="px-4 py-2 bg-brand-600 text-white rounded-lg"
    >
      Show {featureName} toast
    </button>
  );
};
